import React, { useMemo, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { path, prop } from 'ramda'
import { useDispatch } from 'react-redux'

import { Radio } from 'components/patterns'
import { FeatureRevisionSelector } from 'components/feature/FeatureRevision/FeatureRevisionSelector'

import { useSelector } from 'hooks/redux'
import {
  getFeatureEditorMetadata,
  getIsSchoolBusFeatureSubType,
} from 'selectors/feature/featureMetadata'
import { getFeaturesRouteMatch } from 'selectors/router'

import { updateSearchResultItem } from 'actions/deprecated/search'
import { extractFeatureId } from 'utils/uriHelpers'
import strings from 'utils/strings'
import { displayModuleName } from 'i18n/displayConstants'
import {
  FEATURE,
  XMC1,
  XMC2,
  XMC3,
  XMC4,
  XMC5,
  XMC6,
  XMC7,
  XMC8,
  ASAM,
} from 'utils/constants'

const createColumns = ({
  onSelect,
  selected,
  currentFeatureId,
  onRevisionSelect,
}) => [
  {
    dataIndex: 'id',
    width: 20,
    render: (text, record) => {
      const id = prop('id', record)
      const marked = !!selected && selected === id
      return (
        <Radio
          checked={marked}
          disabled={currentFeatureId === id}
          onClick={() => onSelect(record)}
        />
      )
    },
  },
  {
    title: strings.feature_no,
    dataIndex: 'partNumber',
    render: (partNumber, record) => (
      <Link to={`/features/${record.id}`} target="_blank">
        {partNumber}
      </Link>
    ),
    width: 150,
  },
  {
    title: strings.revision_short_dot,
    dataIndex: 'revision',
    render: (revision, feature) => (
      <FeatureRevisionSelector
        revision={revision}
        feature={feature}
        onSelect={(revisionData) => onRevisionSelect(revisionData, feature)}
        navigateOnSelect={false}
        filterConflicts={false}
      />
    ),
    width: 60,
  },
  {
    title: strings.service_description,
    dataIndex: 'description',
  },
  {
    title: strings.status,
    dataIndex: 'status',
    width: 100,
  },
  {
    title: strings.ecu,
    dataIndex: 'moduleName',
    width: 100,
    render: displayModuleName,
  },
  {
    title: strings.release_no,
    dataIndex: 'releaseNumber',
    width: 100,
  },
  {
    title: strings.subgroup,
    dataIndex: 'subGroup',
    width: 100,
  },
]

const moduleNameFilters = {
  [XMC3]: [XMC3, XMC4],
  [XMC4]: [XMC3, XMC4],
  [XMC1]: [XMC1, XMC2],
  [XMC2]: [XMC1, XMC2],
  [ASAM]: [ASAM],
}

const busTypeModuleFilters = [XMC3, XMC4, XMC5, XMC6, XMC7, XMC8]

export default function useBatchImportSearchHook({ selected, onSelect }) {
  const dispatch = useDispatch()
  const match = useSelector(getFeaturesRouteMatch)
  const currentFeatureId = path(['params', 'featureId'], match)

  const handleRevisionSelect = useCallback(
    (revisionData, feature) => {
      const revisionId = extractFeatureId(revisionData.uri)
      dispatch(
        updateSearchResultItem(FEATURE, feature.id, {
          id: revisionId,
          uri: revisionData.uri,
          partNumber: revisionData.partNumber,
          revision: revisionData.revision || revisionData.release,
          status: revisionData.status,
        })
      )
    },
    [dispatch]
  )

  const columns = useMemo(
    () =>
      createColumns({
        selected,
        onSelect,
        currentFeatureId,
        onRevisionSelect: handleRevisionSelect,
      }),
    [selected, onSelect, currentFeatureId, handleRevisionSelect]
  )

  const featureMetadata = useSelector(getFeatureEditorMetadata)
  const isSchoolBusFeature = useSelector(getIsSchoolBusFeatureSubType)
  const { moduleName } = featureMetadata
  const moduleFiltersByVehicleType = isSchoolBusFeature
    ? busTypeModuleFilters
    : moduleNameFilters[moduleName]
  return {
    columns,
    searchFilterModules: moduleFiltersByVehicleType,
    isSchoolBusFeature,
  }
}
